<script setup lang="ts">
import type { FormDto, Locales, StepDto } from '@/services/types/api.types';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import ChoiceList from '@/components/ChoiceList.vue';
import StandardInput from '@/components/StandardInput.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import FormActions from '@/components/FormActions.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';

import { useLanguageValidationErrors } from '@/composables';
import { onBeforeRouteLeave } from 'vue-router';
import { formSaveNavigationGuard, resetFormToItem } from '@/helpers/form.helper';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useFormsStore } from '@/stores';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { localizableString } from '@/helpers/validation.helper';
import { provideEditLocale } from '@/composables/data-display-utils';
import { toRef } from 'vue';

// CONST
const { t } = useI18n();

// PROPS
const props = defineProps<{
  step: StepDto;
  formId: number;
  stepIndex: number;
  permissions: {
    canChoicesBeDeleted?: boolean;
  };
  languageEnabled: Locales[];
}>();

const formsStore = useFormsStore();

// LANGUE
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// FORM
const formContext = useForm({
  validationSchema: yup.object<FormDto>({
    answers: yup.object().shape({
      steps: yup.array().of(
        yup.object().shape({
          maxAnswers: yup.number(),
          otherChoice: yup.boolean().optional(),
          otherChoiceLabel: yup.mixed().when('otherChoice', {
            is: true,
            then: () => localizableString(props.languageEnabled),
            otherwise: () => localizableString(props.languageEnabled, { optional: true, nullable: true }).nullable(),
          }),
        }),
      ),
    }),
  }),
});
const { handleSubmit, errors, meta, resetForm, setFieldValue, values } = formContext;
const languageErrors = useLanguageValidationErrors(errors);

onBeforeRouteLeave(formSaveNavigationGuard(meta));

const formId = toRef(props, 'formId');

const form = useWatchItemLoading<FormDto>(formId, formsStore, (f) => {
  resetFormToItem(formContext, f);
});

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await formsStore.saveForm(values as FormDto),
    () => resetForm({ values: form.value! }),
  );
});

const onCancel = () => {
  if (form.value) {
    resetForm({ values: form.value });
  } else {
    console.warn('onCancel: form is null');
  }
};

const updateOtherChoiceLabel = () => {
  if (!values.answers.steps[props.stepIndex].otherChoice) {
    setFieldValue(`answers.steps[${props.stepIndex}].otherChoiceLabel`, null);
  }
};
</script>

<template>
  <form-actions
    v-model:editLocale="editLocale"
    :onCancel="onCancel"
    :onSubmit="onSubmit"
    :languageErrors="languageErrors"
    :meta="meta"
    :show-language-switcher="true"
    :language-enabled="languageEnabled"
  />
  <div v-if="step">
    <standard-input
      :name="`answers.steps[${props.stepIndex}].maxAnswers`"
      v-if="step.type === 'choices'"
      :label="t('form.step.maxAnswers')"
      type="number"
      outlined
    />
    <standard-toggle
      v-if="step.type === 'choices'"
      :name="`answers.steps[${props.stepIndex}].otherChoice`"
      :label="t('form.step.otherChoice')"
      outlined
      @change="updateOtherChoiceLabel()"
    />
    <localized-input
      :name="`answers.steps[${props.stepIndex}].otherChoiceLabel`"
      :label="t('form.dialogs.matrix-choice.other-label')"
      :editLocale="editLocale"
      outlined
      :language_enabled="languageEnabled"
      type="input"
    />
    <choice-list
      v-if="step.type === 'choices'"
      :rows="[...(step.choices ?? [])].sort((a, b) => a.order_num - b.order_num)"
      :form-id="formId"
      :step-id="step.id!"
      :can-choices-be-deleted="props.permissions.canChoicesBeDeleted"
      :languageEnabled="languageEnabled"
    />
  </div>
</template>
